import {
    AfterViewInit,
    Component,
    computed,
    effect,
    inject,
    Input,
    OnDestroy,
    OnInit,
    signal,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    DomainValidationResult,
    OpportunityEstimateView,
    OpportunityStatusAvailableDto,
    OpportunityUserUserRoleView,
    OpportunityUserView,
    OpportunityView
} from '@api-proxies';
import { OpportunityTermValue } from '@app/models/OpportunityTermValue';
import { EntityTypeCustomFieldService } from '@http/clients/entity-type-custom-field.service';
import { EstimateService } from '@http/clients/estimate.service';
import { OpportunityEstimateService } from '@http/clients/opportunity-estimate.service';
import { OpportunityService } from '@http/clients/opportunity.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { EstimateTypes } from '@models/enums/EstimateTypes';
import { OpportunityAction } from '@models/enums/OpportunityAction';
import { OpportunityStatuses } from '@models/enums/OpportunityStatuses';
import { TabNames } from '@models/enums/TabNames';
import { UserRoles } from '@models/enums/UserRoles';
import { Opportunity } from '@models/Opportunity';
import { OpportunityActualValue } from '@models/OpportunityActualValue';
import { AwaiterService } from '@services/awaiter.service';
import { OrganizationService } from '@services/http/clients/organization.service';
import { TabCounterType } from '@services/tab-counter.service';
import { KebabMenuComponent } from '@shared/kebab-menu/kebab-menu.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ModalCreateEstimateComponent } from '@shared/modals/modal-create-estimate/modal-create-estimate.component';
import { ModalManualStatusComponent } from '@shared/modals/modal-manual-status/modal-manual-status.component';
import { NotesComponent } from '@shared/notes/notes.component';
import {
    OpportunityStatusIndicatorComponent,
    OpportunityStatusIndicatorConfig
} from '@shared/opportunity-status-indicator/opportunity-status-indicator.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { getTab, processTabParameter } from '@utils/tab-navigation';
import {
    CounterBadgeComponent,
    ExternalLinkComponent,
    HelperInfoComponent,
    ModalService,
    ObjectExtensionsService,
    SnackbarNotificationService,
    StatusIndicatorComponent,
    TabItemComponent,
    TabViewComponent
} from 'ngx-lib';
import { Subscription } from 'rxjs';
import { OpportunitiesSingleActivityLogComponent } from './opportunities-single-activity-log/opportunities-single-activity-log.component';
import { OpportunitiesSingleBuildingsComponent } from './opportunities-single-buildings/opportunities-single-buildings.component';
import { OpportunitiesSingleCompaniesComponent } from './opportunities-single-companies/opportunities-single-companies.component';
import { OpportunitiesSingleContactsComponent } from './opportunities-single-contacts/opportunities-single-contacts.component';
import { OpportunitiesSingleEstimatesComponent } from './opportunities-single-estimates/opportunities-single-estimates.component';
import { OpportunitiesSingleOverviewComponent } from './opportunities-single-overview/opportunities-single-overview.component';
import { OpportunitiesSingleTeamComponent } from './opportunities-single-team/opportunities-single-team.component';

@Component({
    selector: 'app-opportunities-single',
    templateUrl: './opportunities-single.component.html',
    styleUrls: ['./opportunities-single.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SectionHeaderComponent,
        CounterBadgeComponent,
        StatusIndicatorComponent,
        HelperInfoComponent,
        ExternalLinkComponent,
        TabViewComponent,
        TabItemComponent,
        KebabMenuComponent,
        OpportunityStatusIndicatorComponent,
        OpportunitiesSingleOverviewComponent,
        OpportunitiesSingleTeamComponent,
        OpportunitiesSingleContactsComponent,
        OpportunitiesSingleEstimatesComponent,
        OpportunitiesSingleActivityLogComponent,
        OpportunitiesSingleCompaniesComponent,
        OpportunitiesSingleBuildingsComponent,
        NotesComponent
    ]
})
export class OpportunitiesSingleComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild(TabViewComponent)
    tabControl!: TabViewComponent;

    @ViewChild('overview')
    overview?: OpportunitiesSingleOverviewComponent;

    opportunity = new Opportunity();

    currentTab = signal<string>(TabNames[TabNames.Overview]);

    isModelEqual = signal(true);

    showButtons = computed(() => this.currentTab() === TabNames[TabNames.Overview] && !this.isModelEqual());

    subtitle?: string;

    opportunityInitialState!: OpportunityView;

    opportunityEstimates: OpportunityEstimateView[] = [];

    baseEstimateExternalUrl = signal<string | undefined>(undefined);

    acumaticaBidUrl = signal<string | undefined>(undefined);

    opportunityStatusId = signal<OpportunityStatuses | null>(null);

    OpportunityStatuses = OpportunityStatuses;

    errors?: DomainValidationResult[];

    isCreateEstimate = false;
    entityTypeId = EntityTypes.Opportunity;
    actualValue = new OpportunityActualValue();
    termValue = new OpportunityTermValue();
    isLoading = false;

    canDisqualified = signal<boolean>(false);

    availableActions = signal<number[]>([]);
    isActive = signal<boolean>(true);
    canEditOpportunity = signal<boolean>(false);
    canArchive = signal<boolean>(false);
    canRestore = signal<boolean>(false);
    canCreateEstimate = signal<boolean>(false);

    tabsCounters: TabCounterType = {
        Contacts: 0,
        Companies: 0,
        Teams: 0,
        Notes: 0,
        Buildings: 0
    };

    availableStatus = signal<OpportunityStatusAvailableDto[]>([]);

    hasAcumaticaIntegration = signal<boolean>(false);

    readonly statusIndicatorConfig = computed<OpportunityStatusIndicatorConfig>(() => ({
        status: this.opportunityStatusId() ?? OpportunityStatuses.Unqualified,
        availableStatus: this.availableStatus(),
        hasLinkedBid: !!this.opportunity.baseEstimateId,
        hasManualStatus: !!this.opportunity.opportunityManualId,
        canDisqualified: this.canDisqualified(),
        showManualStatusOption: !this.baseEstimateExternalUrl() && !this.canDisqualified(),
        canEditOpportunity: this.canEditOpportunity()
    }));

    private opportunityId = 0;
    private organizationId?: number;
    private entitySubscription?: Subscription;

    private readonly opportunityService = inject(OpportunityService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly awaiter = inject(AwaiterService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly objectExtensionService = inject(ObjectExtensionsService);
    private readonly modalService = inject(ModalService);
    private readonly opportunityEstimateService = inject(OpportunityEstimateService);
    private readonly estimateService = inject(EstimateService);
    private readonly entityCustomFieldService = inject(EntityTypeCustomFieldService);
    private readonly organizationService = inject(OrganizationService);

    @Input()
    set id(opportunityId: string | number) {
        const newId = typeof opportunityId === 'string' ? Number.parseInt(opportunityId, 10) : opportunityId;
        if (newId && newId !== this.opportunityId) {
            this.opportunityId = newId;
        }
    }

    @Input()
    set tab(tabParam: string | null | undefined) {
        const newTab = getTab(tabParam ?? null);

        if (!newTab) {
            return;
        }

        if (newTab !== this.currentTab()) {
            if (this.tabControl) {
                this.currentTab.set(processTabParameter(this.route, this.tabControl));
            } else {
                this.currentTab.set(newTab);
            }
        }
    }

    private readonly contactsSyncEffect = effect(async () => {
        const contacts = this.opportunityService.opportunityContacts();

        if (!this.opportunity || !this.opportunityInitialState) {
            return;
        }

        this.opportunity.opportunityContacts = contacts;
        this.opportunityInitialState.opportunityContacts = contacts;
    });

    private readonly companiesSyncEffect = effect(async () => {
        const companies = this.opportunityService.opportunityCompanies();

        if (!this.opportunity || !this.opportunityInitialState) {
            return;
        }

        this.opportunity.opportunityCompanies = companies;
        this.opportunityInitialState.opportunityCompanies = companies;

        const companyWithRole = companies.find((company) =>
            company.opportunityCompanyRoles?.some((role) => role.buildingContactRoleId === 11)
        );
        if (companyWithRole) {
            this.opportunity.companyId = companyWithRole.companyId;
            this.opportunityInitialState.companyId = companyWithRole.companyId;
            this.opportunity.companyName = companyWithRole.companyName;
            this.opportunityInitialState.companyName = companyWithRole.companyName;

            const filteredRoles =
                companyWithRole.opportunityCompanyRoles?.filter((role) =>
                    [1, 2, 3].includes(role.buildingContactRoleId)
                ) ?? [];
            const currentRoles = this.opportunity.opportunityBuildingContactRoles ?? [];
            filteredRoles.forEach((role) => {
                const exists = currentRoles.some(
                    (existing) => existing.buildingContactRoleId === role.buildingContactRoleId
                );
                if (!exists) {
                    currentRoles.push({
                        id: 0,
                        buildingContactRoleId: role.buildingContactRoleId,
                        buildingContactRoleName: role.buildingContactRoleName
                    });
                }
            });
            this.opportunity.opportunityBuildingContactRoles = currentRoles;
            this.opportunityInitialState.opportunityBuildingContactRoles = currentRoles.map((role) => ({ ...role }));
        } else {
            this.opportunity.companyId = undefined;
            this.opportunityInitialState.companyId = undefined;
            this.opportunity.companyName = undefined;
            this.opportunityInitialState.companyName = undefined;
            this.opportunity.opportunityBuildingContactRoles = [];
            this.opportunityInitialState.opportunityBuildingContactRoles = [];
        }
    });

    private readonly usersSyncEffect = effect(async () => {
        const users = this.opportunityService.opportunityUsers();

        if (!this.opportunity || !this.opportunityInitialState) {
            return;
        }

        this.opportunity.opportunityUsers = users;
        this.opportunityInitialState.opportunityUsers = users;

        if (users) {
            const newOpportunitySalespersons = users.filter((opportunity) =>
                opportunity.opportunityUserUserRoles?.some((role) => role.userRoleId === 3)
            );
            this.opportunity.opportunitySalespersons = newOpportunitySalespersons;
            this.opportunityInitialState.opportunitySalespersons = newOpportunitySalespersons;
        }
    });

    private readonly mainBuildingSyncEffect = effect(() => {
        this.opportunityService.mainBuildingId();
        const opportunityView = this.opportunityService.opportunity();

        if (!this.opportunity || !this.opportunityInitialState || !opportunityView) {
            return;
        }

        this.opportunity.buildingId = opportunityView.buildingId;
        this.opportunityInitialState.buildingId = opportunityView.buildingId;
        this.opportunity.buildingName = opportunityView.buildingName;
        this.opportunityInitialState.buildingName = opportunityView.buildingName;
        this.opportunity.buildingIsActive = opportunityView.buildingIsActive;
        this.opportunityInitialState.buildingIsActive = opportunityView.buildingIsActive;
        this.opportunity.line1 = opportunityView.line1;
        this.opportunityInitialState.line1 = opportunityView.line1;
        this.opportunity.line2 = opportunityView.line2;
        this.opportunityInitialState.line2 = opportunityView.line2;
        this.opportunity.addressLines = opportunityView.addressLines;
        this.opportunityInitialState.addressLines = opportunityView.addressLines;
        this.opportunity.zipCode = opportunityView.zipCode;
        this.opportunityInitialState.zipCode = opportunityView.zipCode;
        this.opportunity.countryName = opportunityView.countryName;
        this.opportunityInitialState.countryName = opportunityView.countryName;
        this.opportunity.stateName = opportunityView.stateName;
        this.opportunityInitialState.stateName = opportunityView.stateName;
        this.opportunity.cityName = opportunityView.cityName;
        this.opportunityInitialState.cityName = opportunityView.cityName;
        this.opportunity.fullAddress = opportunityView.fullAddress;
        this.opportunityInitialState.fullAddress = opportunityView.fullAddress;
    });

    ngOnInit(): void {
        this.entitySubscription = this.route.data.subscribe(async ({ entityData }) => {
            this.opportunityInitialState = entityData;
            this.organizationId = entityData.organizationId;
            await this.handleOpportunityChange();
            await this.loadOrganizationData();
        });
    }

    ngAfterViewInit(): void {
        if (this.tabControl) {
            const routeTab = getTab(this.route.snapshot.paramMap.get('tab'));
            if (routeTab && routeTab !== this.currentTab()) {
                this.currentTab.set(processTabParameter(this.route, this.tabControl));
            }
        }
    }

    ngOnDestroy(): void {
        if (this.entitySubscription) {
            this.entitySubscription?.unsubscribe();
        }
    }

    async validateActions(): Promise<void> {
        this.availableActions.set(this.opportunityService.availableActions());

        this.canEditOpportunity.set(this.availableActions().includes(OpportunityAction.Edit));
        this.canArchive.set(this.availableActions().includes(OpportunityAction.Archive));
        this.canRestore.set(this.availableActions().includes(OpportunityAction.Restore));
        this.canCreateEstimate.set(this.availableActions().includes(OpportunityAction.CreateEstimate));
    }

    async saveOpportunity(): Promise<void> {
        this.isCreateEstimate = false;

        const areAllCardsValid = this.overview?.validate();
        if (areAllCardsValid) {
            await this.awaiter.awaitAction(
                'Saving Opportunity',
                async () => {
                    this.isLoading = true;
                    const opportunityToSave = this.overview ? this.overview.opportunity() : this.opportunity;
                    this.mapCustomFieldsRawValue(opportunityToSave);

                    const savedOpportunity = await this.opportunityService.save(opportunityToSave);

                    this.opportunityInitialState = this.objectExtensionService.clone(
                        savedOpportunity
                    ) as OpportunityView;
                    const opportunityFromInitialState = Opportunity.fromInterface(this.opportunityInitialState);
                    this.opportunity = this.objectExtensionService.clone(opportunityFromInitialState) as Opportunity;

                    this.overview?.clearValidationErrors();
                    this.updateIsModelEqual();

                    this.isActive.set(this.opportunityInitialState.isActive);
                    this.baseEstimateExternalUrl.set(this.opportunityInitialState.baseEstimateExternalUrl);
                    this.opportunityStatusId.set(this.opportunityInitialState.opportunityStatusId);
                    this.canDisqualified.set(this.opportunityInitialState.opportunityStatusName === 'Disqualified');

                    await this.onTabCountersChange();

                    this.snackbarNotificationService.success('Opportunity edited successfully.');
                },
                (loading) => (this.isLoading = loading)
            );
        }
    }

    async cancel(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Cancel Editing',
            cancelCaption: 'Continue Edition',
            content: 'Are you sure you want to exit? Your changes will be lost',
            title: 'Unsaved Changes'
        });

        if (!responseOk) return;

        this.opportunity = Opportunity.fromInterface(this.opportunityInitialState);
        this.isCreateEstimate = false;
        this.updateIsModelEqual();
    }

    onValueChanged(): void {
        this.updateIsModelEqual();

        if (!this.isModelEqual()) {
            this.syncSalespersonsWithTeams();
        }
    }

    async validateCreateEstimate(): Promise<boolean> {
        this.isCreateEstimate = true;
        this.errors = await this.opportunityService.validateForEstimateCreation(this.opportunityId);

        if (this.errors?.length === 0) {
            this.isCreateEstimate = false;
        } else {
            this.currentTab.set('Overview');
            setTimeout(() => {
                const areAllCardsValid = this.overview?.validate();
                if (!areAllCardsValid) this.overview?.scrollToFirstErrorElement();
            }, 1);
        }

        return !this.errors?.length;
    }

    async createBaseEstimate(): Promise<void> {
        const validateEstimate = await this.validateCreateEstimate();
        if (!validateEstimate) return;
        const responseOk = await this.modalService.open(ModalCreateEstimateComponent, {
            opportunityId: this.opportunityId
        });

        if (!responseOk) return;

        return await this.awaiter.awaitAction('Creating Base Estimate', async () => {
            this.opportunityStatusId.set(responseOk.opportunityStatusId ?? this.opportunity.opportunityStatusId);
            this.baseEstimateExternalUrl.set(responseOk.externalUrl);
            this.opportunityInitialState.baseEstimateExternalUrl = responseOk.externalUrl;
            this.opportunityInitialState.opportunityStatusId =
                responseOk.opportunityStatusId ?? this.opportunity.opportunityStatusId;
            this.opportunityInitialState.opportunityStatusName = responseOk.opportunityStatusName;
            this.acumaticaBidUrl.set(responseOk.acumaticaBidUrl);
            await this.updateOpportunity();
            await this.updateAvailableStatus();
            await this.updateOpportunityEstimates();
            this.tabChange('Bid');
        });
    }

    async archiveOpportunity(): Promise<void> {
        if (!this.canArchive) return;

        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Archive',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to archive this opportunity?',
            title: 'Archive Opportunity'
        });

        if (!responseOk) return;

        return await this.awaiter.awaitAction('Archiving Opportunity', async () => {
            await this.opportunityService.deactivate(this.opportunity.id);

            await this.opportunityService.loadAvailableActions(this.opportunity.id);

            this.validateActions();
            this.opportunityInitialState.isActive = false;
            this.isActive.set(this.opportunityInitialState.isActive);
            await this.updateOpportunity();
        });
    }

    async unlinkEstimateOpportunity() {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Unlink',
            cancelCaption: 'Cancel',
            content:
                'Are you sure you want to unlink this bid? All the bid information will be removed from the Opportunity.',
            title: 'Unlink Bid'
        });

        if (!responseOk) return;
        const result = await this.awaiter.awaitAction('Unlinking Estimate', async () => {
            const opportunity = await this.opportunityEstimateService.unlinkBaseEstimate(this.opportunity.id);

            this.tabChange('Overview');
            this.opportunityStatusId.set(opportunity.opportunityStatusId);
            this.baseEstimateExternalUrl.set(opportunity.baseEstimateExternalUrl);
            this.opportunityInitialState.baseEstimateExternalUrl = opportunity.baseEstimateExternalUrl;
            this.opportunityInitialState.opportunityStatusId = opportunity.opportunityStatusId;
            this.opportunityInitialState.opportunityStatusName = opportunity.opportunityStatusName;
            await this.updateOpportunity();
            await this.updateOpportunityEstimates();
            this.availableStatus.set([]);
            this.acumaticaBidUrl.set(undefined);
            this.updateIsModelEqual();
        });

        if (!result) {
            await this.updateOpportunityInfo();
        }
    }

    async restoreOpportunity(): Promise<void> {
        if (!this.canRestore) return;

        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Restore',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to restore this opportunity?',
            title: 'Restore Opportunity'
        });

        if (!responseOk) return;

        return await this.awaiter.awaitAction('Restoring Opportunity', async () => {
            await this.opportunityService.activate(this.opportunity.id);
            await this.opportunityService.loadAvailableActions(this.opportunity.id);

            this.validateActions();
            this.opportunityInitialState.isActive = true;
            this.isActive.set(this.opportunityInitialState.isActive);
            await this.updateOpportunity();
        });
    }

    async changeDisqualifiedStatus(): Promise<void> {
        if (!this.canEditOpportunity()) return;

        const newStatus = !this.canDisqualified();
        this.canDisqualified.set(newStatus);
        return await this.awaiter.awaitAction(
            newStatus ? 'Set Disqualified Status' : 'Set Unqualified Status',
            async () => {
                const result = await (newStatus
                    ? this.opportunityService.setDisqualified(this.opportunityId)
                    : this.opportunityService.setUnqualified(this.opportunityId));

                this.opportunityStatusId.set(result.opportunityStatusId);
                this.opportunityInitialState.opportunityStatusId = result.opportunityStatusId;
                this.opportunityInitialState.opportunityStatusName = result.opportunityStatusName;
                await this.updateOpportunity();
            }
        );
    }

    async addManualStatus(): Promise<void> {
        if (!this.canEditOpportunity()) return;

        if (!this.opportunity.opportunityManualId) {
            const validateEstimate = await this.validateCreateEstimate();
            if (!validateEstimate) return;
        }

        const previousStatusId = this.opportunity.opportunityStatusId;
        const previousActualValue = this.opportunity.actualValue;
        const previousActualGrossMargin = this.opportunity.actualGrossMarginPercentage;

        const response = await this.modalService.open(ModalManualStatusComponent, {
            actualValue: this.opportunity.actualValue ?? undefined,
            actualGrossMargin: this.opportunity.actualGrossMarginPercentage ?? undefined,
            opportunityId: this.opportunity.id,
            opportunityStatusId: this.opportunity.opportunityStatusId,
            opportunityManualId: this.opportunity.opportunityManualId ?? undefined
        });

        if (!response) return;

        const hasChanges =
            response.opportunityStatusId !== previousStatusId ||
            response.actualValue !== previousActualValue ||
            response.actualGrossMargin !== previousActualGrossMargin;

        if (!hasChanges) return;

        return await this.awaiter.awaitAction('Adding Manual Status', async () => {
            await this.updateOpportunityInfo();
        });
    }

    async onTabCountersChange(): Promise<void> {
        if (!this.opportunityId) return;
        await this.opportunityService.updateTabCounters(this.entityTypeId, this.opportunityId, this.tabsCounters);
    }

    async handleStatusChangeFromStatusIndicator(status: OpportunityStatusAvailableDto): Promise<void> {
        if (!this.canEditOpportunity() || !this.opportunity.baseEstimateId) return;

        if (status.statusId === OpportunityStatuses.Accepted || status.statusId === OpportunityStatuses.Rejected) {
            const searchEstimate = await this.awaiter.awaitAction(
                'Getting Estimates Info',
                async () => {
                    const estimates = await this.getEstimatesInfoByOpportunity();
                    if (!estimates) return undefined;
                    const alternates = await this.estimateService.getFinalEstimateOverview(estimates);
                    return (alternates?.alternateEstimateViews?.length ?? 0) > 0;
                },
                (loading) => (this.isLoading = loading)
            );

            if (!searchEstimate) {
                const responseOk = await this.modalService.open(ModalConfirmComponent, {
                    acceptCaption: 'Continue',
                    cancelCaption: 'Cancel',
                    content: `The "Selected" Final Estimate does not include any Alternates. Are you sure you want to change the status to "${status.statusName}"?`,
                    title: 'Confirm your action'
                });
                if (!responseOk) return;
            }
        }

        await this.updateEstimateStatus(this.opportunity.baseEstimateId, status.statusId);
    }

    async updateEstimateStatus(estimateId: number, statusId: number): Promise<void> {
        return await this.awaiter.awaitAction(
            'Updating Estimate Status',
            async () => {
                if (estimateId) {
                    const result = await this.estimateService.updateEstimateStatus(estimateId, statusId);

                    this.opportunityStatusId.set(result.opportunityStatusId);
                    this.opportunityInitialState.opportunityStatusId = result.opportunityStatusId;
                    this.opportunityInitialState.opportunityStatusName = result.opportunityStatusName;
                    this.acumaticaBidUrl.set(result.acumaticaBidUrl);

                    await this.updateOpportunity();

                    this.snackbarNotificationService.success('Estimate status updated successfully.');
                }
            },
            (loading) => (this.isLoading = loading)
        );
    }

    async getEstimatesInfoByOpportunity(): Promise<number | null> {
        const opportunityEstimates = await this.opportunityEstimateService.getEstimatesInfoByOpportunity(
            this.opportunityId
        );
        const finalEstimate = opportunityEstimates.find(
            (estimate) => estimate.estimateTypeId === EstimateTypes.Final && estimate.isSelected
        );

        if (finalEstimate) {
            return finalEstimate.estimateId;
        }
        return null;
    }

    tabChange(tabName: string): void {
        this.currentTab.set(tabName);
        this.router.navigate(['/opportunities-single', this.opportunityId, tabName]);
    }

    private async handleOpportunityChange(): Promise<void> {
        if (!this.opportunityId) return;

        await this.awaiter.awaitAction('Getting Opportunity Info', async () => {
            const res = await this.updateOpportunityInfo(this.opportunityInitialState);
            if (res && this.opportunityId) {
                await this.opportunityService.updateTabCounters(
                    this.entityTypeId,
                    this.opportunityId,
                    this.tabsCounters
                );
            }
        });
    }

    private async updateOpportunityInfo(opportunityInitialState?: OpportunityView): Promise<boolean> {
        if (!opportunityInitialState) {
            this.opportunityInitialState = await this.opportunityService.getById(this.opportunityId, true);
        }

        await this.validateActions();

        this.actualValue = OpportunityActualValue.fromInterface(
            await this.opportunityService.actualValues(this.opportunityInitialState.id)
        );
        this.termValue = OpportunityTermValue.fromInterface(
            await this.opportunityService.getTerm(this.opportunityInitialState.id)
        );

        await this.updateOpportunity();

        this.isActive.set(this.opportunityInitialState.isActive);
        this.baseEstimateExternalUrl.set(this.opportunity.baseEstimateExternalUrl);
        this.acumaticaBidUrl.set(this.opportunity.acumaticaBidUrl);
        this.opportunityStatusId.set(this.opportunity.opportunityStatusId);
        this.canDisqualified.set(this.opportunity.opportunityStatusName === 'Disqualified');

        await this.updateAvailableStatus();
        await this.updateOpportunityEstimates();

        return true;
    }

    private async loadOrganizationData(): Promise<void> {
        if (!this.organizationId) return;

        const org = await this.organizationService.getById(this.organizationId);
        this.hasAcumaticaIntegration.set(org.hasAcumaticaIntegration ?? false);
    }

    private async updateOpportunityEstimates(): Promise<void> {
        this.opportunityEstimates = await this.opportunityEstimateService.getEstimatesInfoByOpportunity(
            this.opportunityId
        );
    }

    private mapCustomFieldsRawValue(opportunity: Opportunity): void {
        if (!opportunity.customFieldValues?.length) return;

        opportunity.customFieldValues.forEach((customField) => {
            const raw = customField.rawValue;

            if (raw === undefined || raw === null || raw === '') {
                customField.value = undefined;
                return;
            }

            if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
                customField.value = raw.toString();
                return;
            }

            try {
                customField.value = JSON.stringify(raw);
            } catch {
                customField.value = (raw as unknown as { toString?: () => string })?.toString?.();
            }
        });
    }

    private syncSalespersonsWithTeams(): void {
        const initialSalespersonsList: OpportunityUserView[] = this.objectExtensionService.clone(
            this.opportunityInitialState.opportunitySalespersons ?? []
        ) as OpportunityUserView[];
        const currentSalespersonsList: OpportunityUserView[] = this.objectExtensionService.clone(
            this.opportunity.opportunitySalespersons ?? []
        ) as OpportunityUserView[];
        const areSalespersonsActuallyEqual = this.objectExtensionService.isEqualTo(
            currentSalespersonsList,
            initialSalespersonsList
        );
        if (areSalespersonsActuallyEqual) {
            return;
        }

        const currentUsersList: OpportunityUserView[] = this.objectExtensionService.clone(
            this.opportunity.opportunityUsers ?? []
        ) as OpportunityUserView[];

        currentSalespersonsList.forEach((currentSp) => {
            const userInOpportunity = currentUsersList.find((u) => u.userExternalId === currentSp.userExternalId);

            if (userInOpportunity) {
                currentSp.userId = userInOpportunity.userId;
            }
        });

        const newUsersList: OpportunityUserView[] = this.removeSalespersonsRoleFromUsers(
            currentUsersList,
            currentSalespersonsList
        );

        this.opportunity.opportunityUsers = this.objectExtensionService.clone(newUsersList) as OpportunityUserView[];
        this.opportunity.opportunitySalespersons = this.objectExtensionService.clone(
            currentSalespersonsList
        ) as OpportunityUserView[];
    }

    private removeSalespersonsRoleFromUsers(
        currentUsersList: OpportunityUserView[],
        currentSalespersonsList: OpportunityUserView[]
    ): OpportunityUserView[] {
        return currentUsersList.reduce<OpportunityUserView[]>((result, user) => {
            const salesperson = currentSalespersonsList.find((sp) => sp.userExternalId === user.userExternalId);
            const isSalesPerson = !!salesperson;
            const currentRoles = user.opportunityUserUserRoles ?? [];

            if (isSalesPerson) {
                const hasSalesPersonRole = currentRoles.some((role) => role.userRoleId === UserRoles.SalesPerson);

                if (hasSalesPersonRole) {
                    result.push(user);
                    return result;
                }

                const updatedRoles = [...currentRoles, this.buildSalesPersonRole(user, salesperson)];
                result.push({
                    ...user,
                    opportunityUserUserRoles: updatedRoles
                });
                return result;
            }

            const sanitizedRoles = currentRoles.filter((role) => role.userRoleId !== UserRoles.SalesPerson);
            const hadOnlySalesPersonRole = currentRoles.length > 0 && sanitizedRoles.length === 0;

            if (hadOnlySalesPersonRole) {
                return result;
            }

            result.push({
                ...user,
                opportunityUserUserRoles: sanitizedRoles
            });
            return result;
        }, []);
    }

    private buildSalesPersonRole(
        user: OpportunityUserView,
        salesperson?: OpportunityUserView
    ): OpportunityUserUserRoleView {
        const salesRoleTemplate = salesperson?.opportunityUserUserRoles?.find(
            (role) => role.userRoleId === UserRoles.SalesPerson
        );

        return {
            id: 0,
            opportunityUserId: user.id,
            userRoleId: UserRoles.SalesPerson,
            userRoleCode: salesRoleTemplate?.userRoleCode,
            userRoleDescription: salesRoleTemplate?.userRoleDescription
        };
    }

    private async updateAvailableStatus(): Promise<void> {
        this.availableStatus.set(
            this.opportunity.baseEstimateId && this.canEditOpportunity()
                ? (await this.estimateService.getAvailableStatus(this.opportunity.baseEstimateId)).map((status) => ({
                      ...status,
                      statusName: status.statusName?.replace(/([a-z])([A-Z])/g, '$1 $2')
                  }))
                : []
        );
    }

    private async updateOpportunity(): Promise<void> {
        if (!this.opportunityInitialState.customFieldValues?.length) {
            this.opportunityInitialState.customFieldValues =
                await this.entityCustomFieldService.getCustomFieldsByEntityType(EntityTypes.Opportunity);
        }
        const opportunityFromInitialState = Opportunity.fromInterface(this.opportunityInitialState);
        this.opportunity = this.objectExtensionService.clone(opportunityFromInitialState) as Opportunity;
        this.opportunityService.setOpportunity(this.opportunityInitialState);
    }

    private updateIsModelEqual(): void {
        const opportunityFromInitialState = Opportunity.fromInterface(this.opportunityInitialState);
        this.isModelEqual.set(
            this.objectExtensionService.isEqualTo(this.opportunity, opportunityFromInitialState, ['id'], false)
        );
    }
}
